<template>
    <section class="bg-[#f5f2ec] px-4 py-10 sm:px-5 md:py-28">
        <div class="mx-auto max-w-7xl">
            <div class="max-w-3xl">
                <p class="section-kicker">Contact</p>
                <h2 class="section-title">
                    Start with a conversation about the space you want to build.
                </h2>
                <div class="section-rule"></div>
                <p class="section-copy">
                    Share your site, requirements, budget priorities, and timeline.
                    We will help structure the next step with clarity.
                </p>
            </div>

            <div class="mt-7 grid gap-6 md:mt-12 lg:grid-cols-[0.72fr_1.28fr] lg:gap-16">
                <aside class="border-l border-[#9b8f78] bg-[#f7f2e8] px-4 py-6 shadow-[0_18px_55px_rgba(37,45,41,0.08)] sm:px-8 md:py-8">
                    <div>
                        <p class="text-xs font-semibold uppercase tracking-[0.26em] text-[#7a6f5d]">
                            PHOSPHENE ARCHITECTS
                        </p>
                        <h3 class="mt-3 font-serif text-2xl leading-tight text-[#1f2826] sm:text-4xl md:mt-4">
                            Studio Contact
                        </h3>
                        <p class="mt-4 text-base leading-7 text-[#59615d] md:mt-5 md:leading-8">
                            Architecture, interiors, landscape, and lighting design from
                            Chalakudy, Thrissur.
                        </p>
                    </div>

                    <div class="mt-6 border-t border-[#c9bfad] pt-5 md:mt-8 md:pt-6">
                        <p class="text-xs font-semibold uppercase tracking-[0.22em] text-[#7a6f5d]">
                            Principal Architects
                        </p>
                        <div class="mt-4 space-y-3 text-lg leading-7 text-[#2f3835]">
                            <p v-for="name in principalArchitects" :key="name">
                                {{ name }}
                            </p>
                        </div>
                    </div>

                    <div class="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap md:mt-8">
                        <a
                            class="inline-flex items-center justify-center gap-2 rounded-full bg-[#23362f] px-5 py-3 text-center text-sm font-semibold uppercase tracking-[0.14em] text-[#f7f2e8] transition hover:bg-[#101513]"
                            target="_blank"
                            rel="noopener"
                            href="https://wa.me/+919567746540"
                        >
                            <i class="fab fa-whatsapp"></i>
                            WhatsApp
                        </a>
                        <a
                            class="inline-flex items-center justify-center gap-2 rounded-full border border-[#23362f]/30 px-5 py-3 text-center text-sm font-semibold uppercase tracking-[0.14em] text-[#23362f] transition hover:border-[#23362f] hover:bg-white"
                            target="_blank"
                            rel="noopener"
                            href="https://www.instagram.com/phosphene.architects?igshid=czZidzRzZXpkNmN2"
                        >
                            <i class="fab fa-instagram"></i>
                            Instagram
                        </a>
                    </div>
                </aside>

                <div>
                    <div class="border-y border-[#c9bfad]">
                        <a
                            class="group flex gap-4 border-b border-[#d8cdbb] py-5 transition last:border-b-0 hover:bg-[#fbf8f1] sm:gap-6 md:py-6 md:hover:px-3"
                            v-for="item in contactItems"
                            :key="item.label"
                            :href="item.href"
                            :target="item.external ? '_blank' : undefined"
                            :rel="item.external ? 'noopener' : undefined"
                        >
                            <span class="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#c9bfad] text-[#7a6f5d] transition group-hover:border-[#23362f] group-hover:text-[#23362f]">
                                <i :class="item.icon"></i>
                            </span>
                            <span class="min-w-0">
                                <span class="block text-xs font-semibold uppercase tracking-[0.22em] text-[#7a6f5d]">
                                    {{ item.label }}
                                </span>
                                <span class="mt-2 block break-words text-lg leading-7 text-[#2f3835] transition group-hover:text-[#101513] md:text-xl md:leading-8">
                                    {{ item.text }}
                                </span>
                            </span>
                        </a>

                        <a
                            class="group flex gap-4 py-5 transition hover:bg-[#fbf8f1] sm:gap-6 md:py-6 md:hover:px-3"
                            href="https://www.google.com/maps/search/?api=1&query=Ground%20floor%2C%20Vadakkath%20Residency%2C%20Behind%20Kalabhavan%20Mani%20Park%2C%20Chalakudy%2C%20Thrissur%20680307"
                            target="_blank"
                            rel="noopener"
                        >
                            <span class="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#c9bfad] text-[#7a6f5d] transition group-hover:border-[#23362f] group-hover:text-[#23362f]">
                                <i class="fas fa-location-dot"></i>
                            </span>
                            <span>
                                <span class="block text-xs font-semibold uppercase tracking-[0.22em] text-[#7a6f5d]">
                                    Studio Address
                                </span>
                                <span class="mt-2 block text-base leading-7 text-[#2f3835] transition group-hover:text-[#101513] md:text-lg md:leading-8">
                                    Ground floor, Vadakkath Residency<br />
                                    Behind Kalabhavan Mani Park<br />
                                    Chalakudy, Thrissur - 680307
                                </span>
                            </span>
                        </a>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
export default {
    setup() {
        const principalArchitects = ["Ar. Melvin Davis", "Ar. Anmary Paul"];
        const contactItems = [
            {
                label: "Phone",
                text: "+91 9567746540 / +91 9447831436",
                href: "tel:+919567746540",
                icon: "fas fa-phone",
            },
            {
                label: "Email",
                text: "phosphenearchitects@gmail.com",
                href: "mailto:phosphenearchitects@gmail.com",
                icon: "fas fa-envelope",
            },
            {
                label: "Website",
                text: "www.phosphenearchitects.com",
                href: "https://www.phosphenearchitects.com",
                external: true,
                icon: "fas fa-globe",
            },
            {
                label: "Instagram",
                text: "@phosphene.architects",
                href: "https://www.instagram.com/phosphene.architects?igshid=czZidzRzZXpkNmN2",
                external: true,
                icon: "fab fa-instagram",
            },
        ];

        return { principalArchitects, contactItems };
    },
};
</script>

<style lang="scss" scoped></style>
